import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from "@mui/material";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import CheckboxCell from "../../../components/forms/CheckboxCell";
import BaseTableRow from "../../../components/forms/BaseTableRow";
import ThCell from "../../../components/forms/ThCell";
import TextareaCell from "../../../components/forms/TextareaCell";

import { TEARDOWN_FLAG_LABELS } from "../../../utils/labels";
import {
  useSpecificationStateStore,
  type Change,
  type ChangeTarget,
} from "../conditions/specificationState";
import { QueryKey } from "../../../utils/api";
import { useGetItems } from "../../../hooks/queries/useSpecQueries";
import { useUpdateCellComponent } from "../../../hooks/mutations/useSpecMutations";
import { TEARDOWN_ITEM_PATH, seedTeardownItemTree } from "./teardownState";
import { getMetadata } from "../../../features/metadata/slice";
import parseCellIdString from "../../../utils/parseCellIdString";

/**
 * TeardownFlagsModal
 *
 * FormSpec version of teardown flag editing:
 * - Flags are stored on a separate FormSpec item (teardown item) addressed by its FormSpec `item_uuid`
 * - Flag attribute names in FormSpec are *similar* to labels in `TEARDOWN_FLAG_LABELS`, with minor
 *   differences (dashes/spaces/case). We resolve keys by normalizing strings.
 * - All edits are tracked via `trackChange()` so the existing Item save flow can serialize changes
 *   through `item_changes` -> backend `build_create_item_request`.
 *
 * Important assumption:
 * - Teardown is a floating FormSpec item (not part of the condition BOM tree). Therefore we do NOT
 *   attempt to find a `path` inside the condition. We track attribute edits directly on the teardown
 *   item by using an empty `path` in `ChangeTarget`.
 */

type Props = {
  open: boolean;
  onClose: () => void;

  /**
   * Display title for the modal header.
   * Example: "Update teardown flag specs"
   */
  title?: string;

  /**
   * Teardown cell-component context from Oak/meta.
   *
   * This object should come from the "Teardown" item block in condition metadata (the thing that
   * currently contains `component_meta_mode` and `cell_component_id`).
   *
   * - `component_meta_mode` is the FormSpec teardown item UUID (and may change after save due to versioning)
   * - `cell_component_id` is required by the backend to map the update back to the correct cell component
   */
  teardownItem:
    | {
        component_meta_mode?: string | null;
        cell_component_id?: number | null;
      }
    | null
    | undefined;
};

type TeardownFlagLabelEntry = {
  id: string;
  label: string;
  type?: "boolean" | "text";
};

type ResolvedFlagField = {
  flagId: string; // stable identifier from TEARDOWN_FLAG_LABELS
  label: string; // UI label
  inputType: "boolean" | "text";
  attributeName: string; // actual FormSpec attribute name on the teardown item
  originalValue: any;
};

type TeardownHasChangesRowProps = {
  field: ResolvedFlagField;
  teardownItemPath: string;
  onHasChange: (flagId: string, hasChange: boolean) => void;
};

const TeardownHasChangesRow: React.FC<TeardownHasChangesRowProps> = ({
  field,
  teardownItemPath,
  onHasChange,
}) => {
  const changeTarget: ChangeTarget = useMemo(
    () => ({
      type: "attribute",
      path: teardownItemPath,
      propertyName: field.attributeName,
    }),
    [teardownItemPath, field.attributeName]
  );

  // Subscribe to the store for this exact target so this component re-renders when the change updates.
  const change = useSpecificationStateStore((state) =>
    state.getChange(changeTarget)
  );

  const changed = change !== null;

  React.useEffect(() => {
    onHasChange(field.flagId, changed);
  }, [field.flagId, changed, onHasChange]);

  return null;
};

type TeardownFlagRowProps = {
  field: ResolvedFlagField;
  teardownItemPath: string;
};

const TeardownFlagRow: React.FC<TeardownFlagRowProps> = ({
  field,
  teardownItemPath,
}) => {
  const trackChange = useSpecificationStateStore((state) => state.trackChange);

  const changeTarget: ChangeTarget = useMemo(
    () => ({
      type: "attribute",
      path: teardownItemPath,
      propertyName: field.attributeName,
    }),
    [teardownItemPath, field.attributeName]
  );

  // Subscribe to the change for this specific target (like TrackingInputs) so this row re-renders
  // immediately after `trackChange()`.
  const change = useSpecificationStateStore((state) =>
    state.getChange(changeTarget)
  );

  const currentValue =
    change?.currentValue !== undefined
      ? change.currentValue
      : field.originalValue;

  const displayValue = coerceDisplayValue(field.inputType, currentValue);

  const isBoolean = field.inputType === "boolean";
  const isText = field.inputType === "text";

  // IMPORTANT:
  // `trackChange` uses strict equality to decide if a change should be removed:
  //   if (change.currentValue === change.previousValue) ...
  // If booleans arrive as strings/numbers (e.g. "false"/0) from FormSpec, then we can
  // end up with `previousValue` being "false" and `currentValue` being false, which
  // are not strictly equal. That makes the store think a change still exists (and can
  // also serialize weird values). Normalize both sides for boolean flags.
  const booleanBaselinePreviousValue = isBoolean
    ? Boolean(field.originalValue)
    : field.originalValue;

  return (
    <BaseTableRow key={field.flagId}>
      <ThCell title={field.label}>{field.label}</ThCell>

      {isBoolean ? (
        <CheckboxCell
          checked={Boolean(displayValue)}
          onChange={() => {
            const nextValue = !Boolean(displayValue);

            const nextChange: Change = {
              target: changeTarget,
              operation: "update",
              previousValue: booleanBaselinePreviousValue,
              currentValue: Boolean(nextValue),
              timestamp: Date.now(),
            };

            trackChange(nextChange);
          }}
        />
      ) : isText ? (
        <TextareaCell
          value={displayValue || ""}
          onChange={(e) => {
            const nextValue = e.target.value;

            const nextChange: Change = {
              target: changeTarget,
              operation: "update",
              previousValue: field.originalValue,
              currentValue: nextValue,
              timestamp: Date.now(),
            };

            trackChange(nextChange);
          }}
        />
      ) : (
        <Typography color="text.secondary">Unsupported field</Typography>
      )}
    </BaseTableRow>
  );
};

const normalizeForMatch = (s: string) =>
  s
    .toLowerCase()
    .trim()
    // treat underscores, dashes, and whitespace as equivalent separators
    .replace(/[_\-\s]+/g, " ")
    // drop punctuation that commonly appears in labels
    .replace(/[()'",.:/\\]+/g, "")
    .replace(/\s+/g, " ");

const inferInputTypeFromLabelEntry = (
  entry: TeardownFlagLabelEntry
): "boolean" | "text" => {
  if (entry.type === "text") return "text";
  // Default to boolean as most teardown flags are booleans.
  return "boolean";
};

const inferInputTypeFromFormspecDataType = (
  dataType: string | undefined
): "boolean" | "text" => {
  const dt = (dataType || "").toLowerCase();

  if (dt === "boolean") return "boolean";

  // Everything else we render as text for now (string, number, enums, multiselect, etc.)
  return "text";
};

const coerceDisplayValue = (inputType: "boolean" | "text", value: any) => {
  if (inputType === "boolean") return Boolean(value);
  // text
  return value ?? "";
};

const buildAttributeNameResolver = (
  itemTypeAttributes: Array<{ name: string; data_type?: string }> = []
) => {
  // normalized attribute name -> canonical attribute name from item_type.attributes
  const normalizedAttrNameToCanonical: Record<string, string> = {};
  itemTypeAttributes.forEach((attr) => {
    if (!attr?.name) return;
    normalizedAttrNameToCanonical[normalizeForMatch(attr.name)] = attr.name;
  });

  const resolveByLabel = (label: string, flagId?: string): string | null => {
    // Prefer matching by the slugified flag id (already make_ident-compatible) first.
    // This avoids label substring collisions like:
    // - "Save sludge" vs "Save sludge into vial as standalone sample"
    if (flagId) {
      const byId = normalizedAttrNameToCanonical[normalizeForMatch(flagId)];
      if (byId) return byId;
    }

    // Then try exact normalized match on the human label.
    const byLabel = normalizedAttrNameToCanonical[normalizeForMatch(label)];
    if (byLabel) return byLabel;

    // Do NOT fall back to substring/contains matching; it causes collisions.
    return null;
  };

  return { resolveByLabel };
};

const TeardownFlagsModal: React.FC<Props> = ({
  open,
  onClose,
  title = "Update teardown flag specs",
  teardownItem,
}) => {
  // Note: teardown seeding is handled via `seedTeardownItemTree`, which uses the store directly.
  const dispatch = useDispatch();

  // Save mutation (wires this modal into the same “save item_tree” pattern used by conditions)
  const { mutateAsync: updateCellComponent, isLoading: isSaving } =
    useUpdateCellComponent();

  const queryClient = useQueryClient();

  // Pull the routed cell_id_string directly from params (more robust than sniffing window.location).
  // This is used to refresh Oak/meta condition metadata after save so non-FormSpec UI
  // (e.g. StepInfoBlock flag displays) stays in sync with the latest persisted values.
  const { cell_id_string: cellIdString = "" } = useParams();

  // Use the *prop* teardown item only as an Oak/meta context object for IDs.
  // - component_meta_mode: FormSpec teardown item UUID (may change after save)
  // - cell_component_id: Oak/meta cell component id
  const initialTeardownItemUuid = useMemo(() => {
    const v = teardownItem?.component_meta_mode;
    return typeof v === "string" && v.trim() ? v.trim() : "";
  }, [teardownItem]);

  const cellComponentId = useMemo(() => {
    const v = teardownItem?.cell_component_id;
    return typeof v === "number" ? v : null;
  }, [teardownItem]);

  // Single source-of-truth for the modal:
  // - All rendering uses `effectiveItem`
  // - Store seeding uses `effectiveItem`
  // - After save, we replace `effectiveItem` with the backend-returned payload (new UUID + values)
  const [effectiveItem, setEffectiveItem] = React.useState<ItemWithBOM | null>(
    null
  );

  const effectiveTeardownItemUuid = useMemo(() => {
    const uuid = (effectiveItem as any)?.item?.uuid;
    if (typeof uuid === "string" && uuid.trim()) return uuid.trim();
    return initialTeardownItemUuid;
  }, [effectiveItem, initialTeardownItemUuid]);

  const itemUuids = useMemo(() => {
    const v = effectiveTeardownItemUuid?.trim();
    if (!v) return [];
    // "Generic" is a teardown mode label, not a FormSpec item UUID. Never call
    // `/formspec/items/Generic`.
    if (v.toLowerCase() === "generic") return [];
    return [v];
  }, [effectiveTeardownItemUuid]);

  console.log("[teardown] uuid resolution", {
    initialTeardownItemUuid,
    effectiveTeardownItemUuid,
    cellComponentId,
    itemUuids,
  });

  const { items, isLoading, isError } = useGetItems(itemUuids);

  console.log("[teardown] fetch state", {
    isLoading,
    isError,
    itemsLength: items?.length,
    firstItem: items?.[0],
    firstItemHasItem: !!(items?.[0] as any)?.item,
  });

  // Teardown is a floating item (not part of a real condition BOM on this page).
  // To make `trackChange/getChange` work, we initialize the store with a teardown-rooted item_tree,
  // so root path "" refers to this item.
  const teardownItemPath = TEARDOWN_ITEM_PATH;

  // The “item_tree” source of truth we send to the backend should come from the spec store,
  // since it contains inline *_changes arrays produced by `trackChange`.
  const itemTree = useSpecificationStateStore((state) => state.condition);

  // When the modal opens (or the prop UUID changes), reset the effective item so we start
  // from the latest fetched FormSpec payload.
  useEffect(() => {
    if (!open) return;
    setEffectiveItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTeardownItemUuid]);

  // Populate effective item from fetched query data (only if we haven't overridden it with a save response).
  useEffect(() => {
    if (!open) return;
    if (effectiveItem) return;
    const fetched = items?.[0] ?? null;
    if (!fetched) return;

    setEffectiveItem(fetched);
  }, [open, items, effectiveItem, effectiveTeardownItemUuid]);

  // Seed the store from the effective item (our modal source-of-truth).
  useEffect(() => {
    if (!open) return;
    if (!effectiveItem) return;

    seedTeardownItemTree(effectiveItem);
  }, [open, effectiveItem, effectiveTeardownItemUuid]);

  const fetchError = useMemo(() => {
    if (!open) return null;
    if (!effectiveTeardownItemUuid || !effectiveTeardownItemUuid.trim())
      return "Missing teardown item UUID.";
    if (!cellComponentId) return "Missing cell component id.";
    if (isError) return "Failed to fetch teardown item.";
    return null;
  }, [open, effectiveTeardownItemUuid, cellComponentId, isError]);

  const resolvedFields: ResolvedFlagField[] = useMemo(() => {
    // FormSpec payload shape:
    // {
    //   item: { attributes: { [attributeName]: value }, item_type: { attributes: [{ name, data_type, ...}] } },
    //   bom: [],
    //   processes: []
    // }
    const itemAttributes: Record<string, any> =
      ((effectiveItem as any)?.item as any)?.attributes || {};
    const itemTypeAttributes: Array<{ name: string; data_type?: string }> =
      (((effectiveItem as any)?.item as any)?.item_type?.attributes as any[]) ||
      [];

    const resolver = buildAttributeNameResolver(itemTypeAttributes);

    // Fast lookup for data_type by canonical attribute name
    const dataTypeByName: Record<string, string | undefined> = {};
    itemTypeAttributes.forEach((a) => {
      if (!a?.name) return;
      dataTypeByName[a.name] = a.data_type;
    });

    const result: ResolvedFlagField[] = [];

    (TEARDOWN_FLAG_LABELS as TeardownFlagLabelEntry[]).forEach((entry) => {
      // Resolve the canonical FormSpec attribute name from the item type definition.
      // Prefer matching by slugified flag id to avoid fuzzy label collisions.
      const attributeName = resolver.resolveByLabel(entry.label, entry.id);
      if (!attributeName) return;

      const inputType =
        entry.type !== undefined
          ? inferInputTypeFromLabelEntry(entry)
          : inferInputTypeFromFormspecDataType(dataTypeByName[attributeName]);

      const originalRaw = itemAttributes[attributeName];

      result.push({
        flagId: entry.id,
        label: entry.label,
        inputType,
        attributeName,
        originalValue: coerceDisplayValue(inputType, originalRaw),
      });
    });

    return result;
  }, [effectiveItem]);

  const [hasChangesByFlagId, setHasChangesByFlagId] = React.useState<
    Record<string, boolean>
  >({});

  const hasAnyChanges = useMemo(() => {
    return Object.values(hasChangesByFlagId).some(Boolean);
  }, [hasChangesByFlagId]);

  const canRender =
    !!effectiveTeardownItemUuid &&
    !!effectiveTeardownItemUuid.trim() &&
    !!cellComponentId &&
    !isLoading &&
    !fetchError &&
    !!effectiveItem;

  console.log("[teardown] render gate", {
    effectiveItem,
    fetchError,
    isLoading,
    canRender,
  });

  const renderBody = () => {
    if (!effectiveTeardownItemUuid || !effectiveTeardownItemUuid.trim()) {
      return (
        <Typography color="text.secondary">
          No teardown item UUID specified.
        </Typography>
      );
    }
    if (!cellComponentId) {
      return (
        <Typography color="text.secondary">
          No cell component id specified.
        </Typography>
      );
    }

    if (isLoading) {
      return <CircularProgress />;
    }

    if (fetchError) {
      return <Typography color="error">{fetchError}</Typography>;
    }

    if (!effectiveItem) {
      return (
        <Typography color="text.secondary">
          Teardown item not found in FormSpec.
        </Typography>
      );
    }

    if (resolvedFields.length === 0) {
      return (
        <Typography color="text.secondary">
          No teardown flag attributes were found on this teardown item.
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableBody>
            {resolvedFields.map((field) => (
              <TeardownFlagRow
                key={field.flagId}
                field={field}
                teardownItemPath={teardownItemPath}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const itemHeader = ((effectiveItem as any)?.item as any) || null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box mb={6}>
        <Typography variant="h2">{title}</Typography>

        {itemHeader ? (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Item: <b>{itemHeader.name}</b>
            {itemHeader.arena_part_number ? (
              <>
                {" "}
                — Arena PN: <b>{itemHeader.arena_part_number}</b>
              </>
            ) : null}
          </Typography>
        ) : null}
      </Box>

      {/* Subscribe to per-field store changes (like TrackingInputs) so the Save button + UI
          can reflect WIP state tracked in the store. */}
      {resolvedFields.map((field) => (
        <TeardownHasChangesRow
          key={`change-sub-${field.flagId}`}
          field={field}
          teardownItemPath={teardownItemPath}
          onHasChange={(flagId, hasChange) => {
            setHasChangesByFlagId((prev) => {
              if (prev[flagId] === hasChange) return prev;
              return { ...prev, [flagId]: hasChange };
            });
          }}
        />
      ))}

      {renderBody()}

      <Box mt={6} display="flex" justifyContent="flex-end" gap={2}>
        <Button size="small" color="secondary" onClick={onClose}>
          Close
        </Button>

        <Button
          size="small"
          color="primary"
          onClick={async () => {
            if (!effectiveTeardownItemUuid || !effectiveTeardownItemUuid.trim())
              return;
            if (!cellComponentId) return;
            if (!itemTree) return;

            const response: any = await updateCellComponent({
              item_uuid: effectiveTeardownItemUuid.trim(),
              metadata: {
                cellComponentId,
                item_tree: itemTree,
              },
            });

            // The backend returns the new versioned item + UUID + values. Use it immediately.
            const updatedItem = response?.data ?? response;
            const nextUuidRaw: string | undefined =
              updatedItem?.item?.uuid || updatedItem?.uuid;
            const nextUuid =
              typeof nextUuidRaw === "string" ? nextUuidRaw.trim() : "";

            if (updatedItem?.item) {
              // 1) Update modal source-of-truth
              setEffectiveItem(updatedItem as ItemWithBOM);

              // 2) Update React Query cache so future reads are consistent
              // The teardown item can be versioned on save (new UUID). Keep both the "old" and "new"
              // cache entries in sync so callers still using the old UUID don't render stale values.
              const previousUuid = effectiveTeardownItemUuid.trim();
              const cacheUuid = nextUuid || previousUuid;

              const key = [QueryKey.Items, cacheUuid, "details"] as const;
              queryClient.setQueryData(key, updatedItem);

              if (cacheUuid !== previousUuid) {
                const previousKey = [
                  QueryKey.Items,
                  previousUuid,
                  "details",
                ] as const;
                queryClient.setQueryData(previousKey, updatedItem);
              }

              // 3) Seed store baselines from backend-returned payload
              seedTeardownItemTree(updatedItem as ItemWithBOM);
            }

            // 4) Refresh metadata so StepInfoBlock flag displays update (they read from metadata.values)
            if (cellIdString) {
              const { conditionIdsToRequestedCellIds } =
                parseCellIdString(cellIdString);
              dispatch(getMetadata({ conditionIdsToRequestedCellIds }));
            }

            setHasChangesByFlagId({});
            onClose();
          }}
          disabled={
            !canRender ||
            !hasAnyChanges ||
            isSaving ||
            !cellComponentId ||
            !itemTree
          }
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
};

export default TeardownFlagsModal;
